import React, { useEffect, useState } from 'react';
import SearchFilterStore from 'state/SearchFilterStore';
import { ESearchFilterKeys } from 'constants/SearchFilterTypes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/system';
import { FormInput } from 'components/molecules/FormInput';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import FormWrapper from 'components/atoms/FormWrapper';
import {
  StyledFilterChipWrapper,
  StyledSelectedFiltersWrapper,
} from 'pages/MyFiltersPage/styles';
import { FilterChip } from 'components/atoms/FilterChip';
import { objectToKeyValueArray } from 'helpers/objects';
import { useDictionary } from 'models/dictionaryContext';
import { parseDictionary, parseMultipleDictionaries } from 'helpers/dictionary';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { useTranslations } from 'hooks/useTranslations';
import { useAlert } from 'models/alertContext';
import useSafePresetSchema from 'pages/LegalEntitiesPage/SavePresetPage/schema';
import { EArtAncillaryTypeNameLowerCase } from 'constants/ArtAccessoryType';
import { ActionTypes } from 'state/actions/alert';
import { useSaveAncillaryPreset } from 'pages/AncillariesPage/SaveAncillariesPresetPage/hooks';
import {
  mapValuesToAncillariesPresetRequest,
  permittedAccessoryFilters,
  permittedAmmunitionFilters,
  permittedComponentFilters,
} from 'pages/AncillariesPage/SaveAncillariesPresetPage/helpers';
import { EAncillaryLabels } from 'pages/AncillariesPage/AncillariesReferenceTablePage/DetailAncillariesReference';
import { useLicenceContext } from 'models/licenceContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { mapValuesWithDictionariesHelper } from 'helpers/mapValuesWithDictionariesHelper';
import { ESearchFormLabels as ESearchAccessoryFormLabels } from 'pages/AncillariesPage/AccessoriesPage/AccessoriesSearchForm';
import { ESearchFormLabels as ESearchAmmunitionFormLabels } from 'pages/AncillariesPage/AmmunitionPage/AmmunitionSearchForm';
import { ESearchFormLabels as ESearchComponentFormLabels } from 'pages/AncillariesPage/ComponentsPage/ComponentsSearchForm';

export interface IPresetFormValues {
  name: string;
  emailNotificationEnabled: boolean;
  notificationEnabled: boolean;
}

const initialFormValues = {
  name: '',
  emailNotificationEnabled: false,
  notificationEnabled: false,
};

const SaveAncillariesPresetPage = () => {
  const {
    state,
    getState,
    legality,
    getLegality,
    artAncillaryOptions,
    artAccessoryCalibre,
    artAccessoryComposition,
    artAccessoryHostType,
    artAccessoryLightColour,
    artAccessoryMagnification,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerSuppressor,
    artAccessoryReticle,
    artAccessoryType,
    artAccessoryUtility,
    artAccessoryProofHouse,
    getArtAncillaryOptions,
    getArtAccessoryCalibre,
    getArtAccessoryComposition,
    getArtAccessoryHostType,
    getArtAccessoryLightColour,
    getArtAccessoryMagnification,
    getArtAccessoryManufacturerFlashEliminator,
    getArtAccessoryManufacturerLaserLightModule,
    getArtAccessoryManufacturerMagazine,
    getArtAccessoryManufacturerOpticalSight,
    getArtAccessoryManufacturerSuppressor,
    getArtAccessoryReticle,
    getArtAccessoryType,
    getArtAccessoryUtility,
    getArtAccessoryProofHouse,
    artAmmunitionType,
    artAmmunitionManufacturer,
    artAmmunitionFactory,
    artAmmunitionCalibreGauge,
    artAmmunitionCartridgeComposition,
    artAmmunitionColourOfPrimerSealant,
    artAmmunitionProjectileShape,
    artAmmunitionFunctionalType,
    artAmmunitionShotSize,
    artAmmunitionMunitionType,
    artAmmunitionMunitionVelocity,
    getArtAmmunitionType,
    getArtAmmunitionManufacturer,
    getArtAmmunitionFactory,
    getArtAmmunitionCalibreGauge,
    getArtAmmunitionCartridgeComposition,
    getArtAmmunitionColourOfPrimerSealant,
    getArtAmmunitionProjectileShape,
    getArtAmmunitionFunctionalType,
    getArtAmmunitionShotSize,
    getArtAmmunitionMunitionType,
    getArtAmmunitionMunitionVelocity,
    artComponentType,
    artComponentManufacturer,
    artComponentCalibre,
    artComponentCapacity,
    artComponentProofHouse,
    getArtComponentType,
    getArtComponentManufacturer,
    getArtComponentCalibre,
    getArtComponentCapacity,
    getArtComponentProofHouse,
    artComposition,
    getArtComposition,
    make,
    getMake,
    model,
    getModel,
    firearmType,
    getFirearmType,
    manufacturer,
    getManufacturer,
  } = useDictionary();
  const { t } = useTranslations();
  const { schema } = useSafePresetSchema();

  const [filtersArray, setFiltersArray] = useState<{ key: string; value: string }[]>([]);
  const { type } = useParams<{ type: ESearchFilterKeys | EArtAncillaryTypeNameLowerCase }>();
  const [error, setError] = useState<string>('');
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { savePreset } = useSaveAncillaryPreset();
  const { setAlert } = useAlert();
  const { isOnlineLicence } = useLicenceContext();
  const { longDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IPresetFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IPresetFormValues>,
  });
  const filters = SearchFilterStore.getValue(type as ESearchFilterKeys);
  const { state: routerState }: any = useLocation();

  const filterPassedValues = (parsedFilters: { key: string; value: string }[]) => {
    switch (type) {
      case EArtAncillaryTypeNameLowerCase.ACCESSORY: {
        return parsedFilters.filter((record) =>
          permittedAccessoryFilters.includes(record.key),
        );
      }
      case EArtAncillaryTypeNameLowerCase.AMMUNITION: {
        return parsedFilters.filter((record) =>
          permittedAmmunitionFilters.includes(record.key),
        );
      }
      case EArtAncillaryTypeNameLowerCase.COMPONENTS: {
        return parsedFilters.filter((record) =>
          permittedComponentFilters.includes(record.key),
        );
      }
      default:
        return [];
    }
  };

  const filtersWithDictionaries = () => {
    switch (type) {
      case EArtAncillaryTypeNameLowerCase.ACCESSORY:
      case EArtAncillaryTypeNameLowerCase.AMMUNITION:
      case EArtAncillaryTypeNameLowerCase.COMPONENTS:
        return setFiltersArray(
          mapValuesWithDictionariesHelper(filterPassedValues(objectToKeyValueArray(filters)), {
            ancillaryTypeUniqueId: parseMultipleDictionaries(
              [artAccessoryType, artAmmunitionType, artComponentType],
              'name',
            ),
            manufacturerUniqueId: parseMultipleDictionaries(
              [
                artAccessoryManufacturerMagazine,
                artAccessoryManufacturerOpticalSight,
                artAccessoryManufacturerSuppressor,
                artAccessoryManufacturerFlashEliminator,
                artAccessoryManufacturerLaserLightModule,
                artAmmunitionManufacturer,
                artComponentManufacturer,
              ],
              'name',
            ),
            manufacturersUniqueIds: parseMultipleDictionaries(
              [
                artAccessoryManufacturerMagazine,
                artAccessoryManufacturerOpticalSight,
                artAccessoryManufacturerSuppressor,
                artAccessoryManufacturerFlashEliminator,
                artAccessoryManufacturerLaserLightModule,
                artAmmunitionManufacturer,
                artComponentManufacturer,
              ],
              'name',
            ),
            calibresUniqueIds: parseMultipleDictionaries(
              [artAccessoryCalibre, artAmmunitionCalibreGauge, artComponentCalibre],
              'name',
            ),
            hostTypesUniqueIds: parseDictionary(artAccessoryHostType, 'name'),
            magnificationsUniqueIds: parseDictionary(artAccessoryMagnification, 'name'),
            utilitiesUniqueIds: parseDictionary(artAccessoryUtility, 'name'),
            reticlesUniqueIds: parseDictionary(artAccessoryReticle, 'name'),
            lightColoursUniqueIds: parseDictionary(artAccessoryLightColour, 'name'),
            compositionsUniqueIds: parseMultipleDictionaries(
              [artAccessoryComposition, artComposition],
              'name',
            ),
            proofHousesUniqueIds: parseMultipleDictionaries(
              [artAccessoryProofHouse, artComponentProofHouse],
              'name',
            ),
            statesUniqueIds: parseDictionary(state, 'name'),
            legalityUniqueId: parseDictionary(legality, 'name'),
            componentCapacitiesUniqueIds: parseDictionary(artComponentCapacity, 'name'),
            frtMakesUniqueIds: parseDictionary(make, 'name'),
            frtModelsUniqueIds: parseDictionary(model, 'name'),
            frtTypesUniqueIds: parseDictionary(firearmType, 'name'),
            frtManufacturersUniqueIds: parseDictionary(manufacturer, 'name'),
            factoriesUniqueIds: parseDictionary(artAmmunitionFactory, 'name'),
            cartridgeCompositionsUniqueIds: parseDictionary(
              artAmmunitionCartridgeComposition,
              'name',
            ),
            coloursOfPrimerSealantUniqueIds: parseDictionary(
              artAmmunitionColourOfPrimerSealant,
              'name',
            ),
            projectileShapesUniqueIds: parseDictionary(artAmmunitionProjectileShape, 'name'),
            functionalTypesUniqueIds: parseDictionary(artAmmunitionFunctionalType, 'name'),
            munitionTypeUniqueId: parseDictionary(artAmmunitionMunitionType, 'name'),
            shotSizesUniqueIds: parseDictionary(artAmmunitionShotSize, 'name'),
            munitionVelocitiesUniqueIds: parseDictionary(
              artAmmunitionMunitionVelocity,
              'name',
            ),
          }),
        );
      default:
        return [];
    }
  };

  useEffect(() => {
    (async function init() {
      if (type === EArtAncillaryTypeNameLowerCase.ACCESSORY) {
        if (!state) {
          getState();
        }

        if (!legality) {
          getLegality();
        }

        if (!artAncillaryOptions) {
          getArtAncillaryOptions();
        }

        if (!artAccessoryCalibre) {
          getArtAccessoryCalibre();
        }

        if (!artAccessoryComposition) {
          getArtAccessoryComposition();
        }

        if (!artAccessoryHostType) {
          getArtAccessoryHostType();
        }

        if (!artAccessoryLightColour) {
          getArtAccessoryLightColour();
        }

        if (!artAccessoryMagnification) {
          getArtAccessoryMagnification();
        }

        if (!artAccessoryManufacturerFlashEliminator) {
          getArtAccessoryManufacturerFlashEliminator();
        }

        if (!artAccessoryManufacturerLaserLightModule) {
          getArtAccessoryManufacturerLaserLightModule();
        }

        if (!artAccessoryManufacturerMagazine) {
          getArtAccessoryManufacturerMagazine();
        }

        if (!artAccessoryManufacturerOpticalSight) {
          getArtAccessoryManufacturerOpticalSight();
        }

        if (!artAccessoryManufacturerSuppressor) {
          getArtAccessoryManufacturerSuppressor();
        }

        if (!artAccessoryReticle) {
          getArtAccessoryReticle();
        }

        if (!artAccessoryType) {
          getArtAccessoryType();
        }

        if (!artAccessoryUtility) {
          getArtAccessoryUtility();
        }

        if (!artAccessoryProofHouse) {
          getArtAccessoryProofHouse();
        }
      } else if (type === EArtAncillaryTypeNameLowerCase.AMMUNITION) {
        if (!artAncillaryOptions) {
          getArtAncillaryOptions();
        }

        if (!artAmmunitionType) {
          getArtAmmunitionType();
        }

        if (!artAmmunitionManufacturer) {
          getArtAmmunitionManufacturer();
        }

        if (!artAmmunitionFactory) {
          getArtAmmunitionFactory();
        }

        if (!artAmmunitionCalibreGauge) {
          getArtAmmunitionCalibreGauge();
        }

        if (!artAmmunitionCartridgeComposition) {
          getArtAmmunitionCartridgeComposition();
        }

        if (!artAmmunitionColourOfPrimerSealant) {
          getArtAmmunitionColourOfPrimerSealant();
        }

        if (!artAmmunitionProjectileShape) {
          getArtAmmunitionProjectileShape();
        }

        if (!artAmmunitionFunctionalType) {
          getArtAmmunitionFunctionalType();
        }

        if (!artAmmunitionShotSize) {
          getArtAmmunitionShotSize();
        }

        if (!artAmmunitionMunitionType) {
          getArtAmmunitionMunitionType();
        }

        if (!artAmmunitionMunitionVelocity) {
          getArtAmmunitionMunitionVelocity();
        }
      } else if (type === EArtAncillaryTypeNameLowerCase.COMPONENTS) {
        if (!state) {
          getState();
        }
        if (!artAncillaryOptions) {
          getArtAncillaryOptions();
        }

        if (!artComponentType) {
          getArtComponentType();
        }

        if (!artComponentManufacturer) {
          getArtComponentManufacturer();
        }

        if (!artComponentCalibre) {
          getArtComponentCalibre();
        }

        if (!artComponentCapacity) {
          getArtComponentCapacity();
        }

        if (!artComponentProofHouse) {
          getArtComponentProofHouse();
        }
        if (!artComposition) {
          getArtComposition();
        }
        if (!make) {
          getMake();
        }
        if (!model) {
          getModel();
        }
        if (!firearmType) {
          getFirearmType();
        }
        if (!manufacturer) {
          getManufacturer();
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (filters) {
      filtersWithDictionaries();
    }
  }, [
    state,
    legality,
    artAncillaryOptions,
    artAccessoryCalibre,
    artAccessoryComposition,
    artAccessoryHostType,
    artAccessoryLightColour,
    artAccessoryMagnification,
    artAccessoryManufacturerFlashEliminator,
    artAccessoryManufacturerLaserLightModule,
    artAccessoryManufacturerMagazine,
    artAccessoryManufacturerOpticalSight,
    artAccessoryManufacturerSuppressor,
    artAccessoryReticle,
    artAccessoryType,
    artAccessoryUtility,
    artAccessoryProofHouse,
    artAmmunitionType,
    artAmmunitionManufacturer,
    artAmmunitionFactory,
    artAmmunitionCalibreGauge,
    artAmmunitionCartridgeComposition,
    artAmmunitionColourOfPrimerSealant,
    artAmmunitionProjectileShape,
    artAmmunitionFunctionalType,
    artAmmunitionShotSize,
    artAmmunitionMunitionType,
    artAmmunitionMunitionVelocity,
    artComponentType,
    artComponentManufacturer,
    artComponentCalibre,
    artComponentCapacity,
    artComponentProofHouse,
  ]);

  const getBackRoute = () => {
    switch (type) {
      case EArtAncillaryTypeNameLowerCase.ACCESSORY:
        return `${ERouteLinks.Ancillaries}?expand=accessories`;
      case EArtAncillaryTypeNameLowerCase.AMMUNITION:
        return `${ERouteLinks.Ancillaries}?expand=ammunition`;
      case EArtAncillaryTypeNameLowerCase.COMPONENTS:
        return `${ERouteLinks.Ancillaries}?expand=components`;

      default:
        return ERouteLinks.Ancillaries;
    }
  };

  const getLabelsByType = () => {
    switch (type) {
      case EArtAncillaryTypeNameLowerCase.ACCESSORY:
        return { ...EAncillaryLabels, ...ESearchAccessoryFormLabels };
      case EArtAncillaryTypeNameLowerCase.AMMUNITION:
        return { ...EAncillaryLabels, ...ESearchAmmunitionFormLabels };
      case EArtAncillaryTypeNameLowerCase.COMPONENTS:
        return { ...EAncillaryLabels, ...ESearchComponentFormLabels };
      default:
        return EAncillaryLabels;
    }
  };

  const labels = getLabelsByType();

  useEffect(() => {
    if (filters === null) {
      navigate(getBackRoute());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onSubmitForm = async (data: IPresetFormValues) => {
    if (type) {
      setIsSubmitLoading(true);
      const body = mapValuesToAncillariesPresetRequest(filters, type, longDateFormat);

      if (body) {
        try {
          const res = await savePreset({
            name: data.name,
            emailNotificationEnabled: data.emailNotificationEnabled,
            notificationEnabled: data.notificationEnabled,
            type,
            body,
          });
          if (res.status === 200) {
            setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
              text: t('saveMyFilter.save.success.text'),
              variant: EAlertVariants.success,
            });
            return navigate(`${ERouteLinks.MyFilters}`);
          }

          setError(res.response.data.message);
        } catch (e: any) {
          console.error(e);
        }
      }
    }
    return setIsSubmitLoading(false);
  };

  const getBreadcrumbsLabel = () => {
    let label = '';
    if (type) {
      switch (type) {
        case EArtAncillaryTypeNameLowerCase.ACCESSORY:
          label = t('ancillaries.accessories.accordion.name');
          break;
        case EArtAncillaryTypeNameLowerCase.AMMUNITION:
          label = t('ancillaries.ammunition.accordion.name');
          break;
        case EArtAncillaryTypeNameLowerCase.COMPONENTS:
          label = t('ancillaries.components.accordion.name');
          break;
        default:
          break;
      }
    }
    return label;
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.ancillaries.name'), route: ERouteLinks.Ancillaries },
          {
            label: getBreadcrumbsLabel(),
            route: getBackRoute(),
          },
          t('saveMyFilter.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('saveMyFilter.header')}</StyledPageTitle>

      <FormWrapper>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box sx={{ mb: 1 }}>
                <FormInput
                  name="name"
                  label={`${t('saveMyFilter.filterName.label')}*`}
                  placeholder={t('saveMyFilter.filterName.label')}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <StyledSelectedFiltersWrapper container style={{ rowGap: 1 }}>
                <Grid>
                  <Typography variant="body2">{`${t(
                    'saveMyFilter.selectedCriteria.section',
                  )}:`}</Typography>
                </Grid>
                <StyledFilterChipWrapper>
                  {filtersArray &&
                    filtersArray.map((item: { key: string; value: string }) => {
                      if (
                        item &&
                        item.key === 'ownerUniqueId' &&
                        routerState &&
                        routerState.additionalSaveFilterValues.owner !== undefined
                      ) {
                        item.value = routerState.additionalSaveFilterValues.owner.label;
                      }
                      if (
                        item &&
                        item.key === 'keeperUniqueId' &&
                        routerState &&
                        routerState.additionalSaveFilterValues.keeper !== undefined
                      ) {
                        item.value = routerState.additionalSaveFilterValues.keeper.label;
                      }
                      return (
                        <FilterChip
                          key={item.key}
                          name={item.key}
                          label={t(labels?.[item.key as keyof typeof labels]) || item.key}
                          values={item.value}
                        />
                      );
                    })}
                </StyledFilterChipWrapper>
              </StyledSelectedFiltersWrapper>
            </Grid>
            <Grid size={{ xs: 12 }} container columnSpacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormCheckbox
                  name="notificationEnabled"
                  label={t('saveMyFilter.notifyMeViaSystem.checkbox.label')}
                  control={control}
                  errors={errors}
                />
              </Grid>
              {isOnlineLicence() && (
                <Grid size={{ xs: 12 }}>
                  <FormCheckbox
                    name="emailNotificationEnabled"
                    label={t('saveMyFilter.notifyMeViaEmail.checkbox.label')}
                    control={control}
                    errors={errors}
                  />
                </Grid>
              )}
            </Grid>
            <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('saveMyFilter.save.button')}
                  variant={EButtonVariants.contained}
                  isLoading={isSubmitLoading}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  variant={EButtonVariants.outlined}
                  onClick={() =>
                    navigate(routerState.cancelPath || getBackRoute(), {
                      state: { uniqueIds: routerState?.additionalSaveFilterValues.uniqueIds },
                    })
                  }
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
            {error && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Alert text={error} variant={EAlertVariants.error} />
              </Grid>
            )}
          </Grid>
        </form>
      </FormWrapper>
    </>
  );
};

export { SaveAncillariesPresetPage };
